import { toast } from "sonner";
import { IconEye, IconEyeOff } from "@tabler/icons-react";
import { useFleetReport, useSetWeightsRelease } from "@/api/hooks/use-fleet-report";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/providers/auth-provider";
import { asEventId } from "@/types/identifiers";
import { formatSignedKg } from "@/domain/weigh-in";
import { PageHeader } from "@/components/ui/page-header";
import { StatCard } from "@/components/stat-card";
import { TeamFlag } from "@/components/team-flag";
import { CheckedCard } from "@/pages/fleet-measurements/_shared";
import { fmt } from "@/lib/utils";
import { formatDate, formatMonthYear } from "@/lib/time";
import type { components } from "@/api/generated/schema";

type ReportEntry = components["schemas"]["FleetWeightReportEntry-Output"];
type ReportSummary = components["schemas"]["FleetWeightReportSummary"];
type CustomItem = components["schemas"]["FleetReportCustomItem"];

/** "Battery +12.5 kg · Oil pump −4 kg" — signed per direction. */
function customItemsSummary(items: CustomItem[]): string {
  return items
    .map((i) => `${i.label} ${formatSignedKg(i.weight_kg, i.direction === "subtract")}`)
    .join(" · ");
}

const MISSING_LABELS: Record<string, string> = {
  platform: "missing platform",
  "wing-apw": "missing wing",
};

// Row cues: final weight tinted vs the fleet mean, TCG flagged when visibly
// off-centre. The weight band is the tolerance the summary card gauges against
// (0.5% of the heaviest boat, ~12 kg) rather than a second, tighter number —
// two bands on one page tinted boats amber that the summary called in spec.
const TCG_CUE_BAND_M = 0.015;
const EMERALD_CUE =
  "bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200";
const AMBER_CUE = "bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200";

function weightCueClass(
  v: number | null | undefined,
  mean: number | null | undefined,
  band: number | null | undefined,
): string {
  if (v == null || mean == null || band == null) return "";
  return Math.abs(v - mean) <= band ? EMERALD_CUE : AMBER_CUE;
}

/** "POR S6" when the last weighing falls in a coded event, else its month. */
function lastWeighedLabel(e: ReportEntry): string {
  if (e.last_weighed_event_code && e.last_weighed_season != null) {
    return `${e.last_weighed_event_code} S${e.last_weighed_season}`;
  }
  return e.last_weighed_at ? formatMonthYear(e.last_weighed_at) : "—";
}

function tcgCueClass(v: number | null | undefined): string {
  if (v == null || Math.abs(v) <= TCG_CUE_BAND_M) return "";
  return AMBER_CUE;
}

function SummaryCards({ summary }: { summary: ReportSummary }) {
  const weightSpread = summary.final_weight_kg_spread;
  // Tolerance is 0.5% of the heaviest boat (≈12 kg), not a % of the fleet mean.
  const weightTol = summary.weight_tolerance_kg;
  const weightOk = weightSpread != null && weightTol != null && weightSpread <= weightTol;
  const lcgSpread = summary.final_lcg_m_spread;
  const lcgOk = lcgSpread != null && lcgSpread <= summary.lcg_spread_target_m;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <StatCard
        label="Fleet weight spread"
        value={weightSpread != null ? `${weightSpread.toFixed(0)} kg` : "—"}
        accent={weightOk ? "border-l-emerald-500" : "border-l-amber-500"}
        secondary={
          weightTol == null
            ? undefined
            : weightOk
              ? `within ${summary.weight_spread_target_pct}% of heaviest (≤ ${weightTol.toFixed(0)} kg)`
              : `target ≤ ${weightTol.toFixed(0)} kg · ${summary.weight_spread_target_pct}% of heaviest`
        }
        secondaryClass={
          weightOk
            ? "text-emerald-600 dark:text-emerald-400"
            : "text-amber-600 dark:text-amber-400"
        }
      />
      <StatCard
        label="Final LCG spread"
        value={lcgSpread != null ? `${lcgSpread.toFixed(2)} m` : "—"}
        accent={lcgOk ? "border-l-emerald-500" : "border-l-amber-500"}
        secondary={`target ≤ ${summary.lcg_spread_target_m.toFixed(2)} m`}
        secondaryClass={
          lcgOk
            ? "text-emerald-600 dark:text-emerald-400"
            : "text-amber-600 dark:text-amber-400"
        }
      />
      <CheckedCard checked={summary.weighed_count} total={summary.boat_count} label="Weighed" />
      <StatCard
        label="Total correctors"
        value={`${summary.corrector_total_kg.toFixed(0)} kg`}
        accent="border-l-border"
        secondary="installed fleet-wide"
      />
    </div>
  );
}

function BoatChips({ entry }: { entry: ReportEntry }) {
  return (
    <>
      {entry.is_reference && (
        <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300">
          REF
        </span>
      )}
      {entry.missing.map((m) => (
        <span
          key={m}
          className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300"
        >
          {MISSING_LABELS[m] ?? m}
        </span>
      ))}
      {entry.platform_gate_ok === false && (
        <span
          className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300"
          title="Platform weighing signed off outside the repeatability gate"
        >
          GATE
        </span>
      )}
      {entry.custom_items.length > 0 && (
        <span
          className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-600 dark:bg-slate-800/70 dark:text-slate-300"
          title={`Custom items folded into platform kg — ${customItemsSummary(entry.custom_items)}`}
        >
          {entry.custom_items.length} item{entry.custom_items.length === 1 ? "" : "s"}
        </span>
      )}
    </>
  );
}

const NUM = "px-3 py-2.5 text-right tabular-nums";
const SEAM = "border-l border-border";

function ReportTable({ entries, summary }: { entries: ReportEntry[]; summary: ReportSummary }) {
  const mean = summary.final_weight_kg_mean;
  return (
    <div className="bg-card rounded-lg border border-border overflow-x-auto">
      <table className="w-full text-[13px] whitespace-nowrap">
        <thead>
          <tr className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground border-b border-border">
            <th colSpan={3} className="px-3 pt-2.5 pb-1" />
            <th colSpan={4} className={`px-3 pt-2.5 pb-1 text-left ${SEAM}`}>
              Weighing
            </th>
            <th colSpan={4} className={`px-3 pt-2.5 pb-1 text-left ${SEAM}`}>
              Correctors
            </th>
            <th colSpan={4} className={`px-3 pt-2.5 pb-1 text-left ${SEAM}`}>
              Final
            </th>
          </tr>
          <tr className="text-xs text-muted-foreground border-b border-border [&>th]:px-3 [&>th]:py-2 [&>th]:font-medium">
            <th className="text-left">Boat</th>
            <th className="text-left">Team</th>
            <th className="text-left">Last weighed</th>
            <th className={`text-right ${SEAM}`}>Platform kg</th>
            <th className="text-right">Wing kg</th>
            <th className="text-right">Total kg</th>
            <th className="text-right">LCG m</th>
            <th className={`text-right ${SEAM}`}>Port kg</th>
            <th className="text-right">Stbd kg</th>
            <th className="text-right">Tot kg</th>
            <th className="text-left">Position</th>
            <th className={`text-right ${SEAM}`}>LCG m</th>
            <th className="text-right">TCG m</th>
            <th className="text-right">VCG m</th>
            <th className="text-right">Weight kg</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => {
            const showCorrectors = e.has_data || e.total_corrector_kg > 0;
            return (
              <tr key={e.boat_id} className="border-b border-border/60 even:bg-muted/20">
                <td className="px-3 py-2.5 font-semibold">
                  {e.boat_number}
                  <BoatChips entry={e} />
                </td>
                <td className="px-3 py-2.5 font-medium">
                  <span className="inline-flex items-center gap-1.5">
                    <TeamFlag iso_alpha2={e.team_iso_alpha2} size="xs" />
                    {e.boat_code}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-muted-foreground">
                  {lastWeighedLabel(e)}
                </td>
                <td className={`${NUM} ${SEAM}`}>{fmt(e.platform_kg, 0)}</td>
                <td className={NUM}>{fmt(e.wing_kg, 0)}</td>
                <td className={`${NUM} font-semibold`}>{fmt(e.total_kg, 0)}</td>
                <td className={NUM}>{fmt(e.total_lcg_m)}</td>
                <td className={`${NUM} ${SEAM} ${e.port_corrector_kg === 0 ? "text-muted-foreground" : ""}`}>
                  {showCorrectors ? e.port_corrector_kg.toFixed(0) : "—"}
                </td>
                <td className={`${NUM} ${e.stbd_corrector_kg === 0 ? "text-muted-foreground" : ""}`}>
                  {showCorrectors ? e.stbd_corrector_kg.toFixed(0) : "—"}
                </td>
                <td className={`${NUM} font-semibold`}>
                  {showCorrectors ? e.total_corrector_kg.toFixed(0) : "—"}
                </td>
                <td className="px-3 py-2.5">{e.cw_position ?? "—"}</td>
                <td className={`${NUM} ${SEAM} font-semibold`}>{fmt(e.final_lcg_m)}</td>
                <td className={`${NUM} ${tcgCueClass(e.final_tcg_m)}`}>{fmt(e.final_tcg_m, 3)}</td>
                <td className={NUM}>{fmt(e.final_vcg_m)}</td>
                <td className={`${NUM} font-semibold ${weightCueClass(e.final_weight_kg, mean, summary.weight_tolerance_kg)}`}>
                  {fmt(e.final_weight_kg, 0)}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-foreground/20 text-xs [&>td]:px-3 [&>td]:py-2.5">
            <td colSpan={3} className="font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">
              Fleet mean / spread
              {summary.missing_boats.length > 0 && (
                <span className="normal-case font-normal tracking-normal ml-2">
                  {summary.missing_boats.join(", ")} excluded
                </span>
              )}
            </td>
            <td className={SEAM} />
            <td />
            <td className="text-right tabular-nums font-semibold">
              {fmt(summary.total_kg_mean, 0)}
              <span className="text-muted-foreground font-normal"> / {fmt(summary.total_kg_spread, 0)}</span>
            </td>
            <td />
            <td className={SEAM} colSpan={4} />
            <td className={`${SEAM} text-right tabular-nums font-semibold`}>
              {fmt(summary.final_lcg_m_mean)}
              <span className="text-muted-foreground font-normal"> / {fmt(summary.final_lcg_m_spread)}</span>
            </td>
            <td colSpan={2} />
            <td className="text-right tabular-nums font-semibold">
              {fmt(summary.final_weight_kg_mean, 0)}
              <span className="text-muted-foreground font-normal"> / {fmt(summary.final_weight_kg_spread, 0)}</span>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function MobileList({ entries, summary }: { entries: ReportEntry[]; summary: ReportSummary }) {
  return (
    <div className="space-y-2">
      {entries.map((e) => (
        <div key={e.boat_id} className="bg-card border-l-3 border-l-border rounded-r-lg pl-4 pr-5 py-3">
          <div className="flex items-center gap-2 mb-2">
            <TeamFlag iso_alpha2={e.team_iso_alpha2} size="xs" />
            <span className="font-bold">{e.boat_code}</span>
            <span className="text-xs text-muted-foreground">{e.boat_number}</span>
            <BoatChips entry={e} />
            <span className="ml-auto text-xs text-muted-foreground">
              {lastWeighedLabel(e)}
            </span>
          </div>
          <div className="grid grid-cols-4 gap-2 text-center">
            {(
              [
                ["Total kg", fmt(e.total_kg, 0)],
                ["Corr kg", e.total_corrector_kg.toFixed(0)],
                ["Final LCG", fmt(e.final_lcg_m)],
                ["Final kg", fmt(e.final_weight_kg, 0)],
              ] as const
            ).map(([label, value]) => (
              <div key={label}>
                <div className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {label}
                </div>
                <div className="text-sm font-semibold tabular-nums">{value}</div>
              </div>
            ))}
          </div>
          {e.custom_items.length > 0 && (
            <div className="mt-2 border-t border-border/60 pt-1.5 text-[11px] text-muted-foreground">
              <span className="font-semibold uppercase tracking-wider text-[9px]">Items</span>{" "}
              {customItemsSummary(e.custom_items)}
            </div>
          )}
        </div>
      ))}
      <div className="text-xs text-muted-foreground text-center pt-1">
        Fleet mean {fmt(summary.final_weight_kg_mean, 0)} kg · spread{" "}
        {fmt(summary.final_weight_kg_spread, 0)} kg
      </div>
    </div>
  );
}

function PublishButton({
  eventId,
  eventName,
  releasedAt,
}: {
  eventId: string | null;
  eventName: string | null;
  releasedAt: string | null;
}) {
  const setRelease = useSetWeightsRelease(eventId ? asEventId(eventId) : null);
  const published = releasedAt != null;
  const label = eventName ?? "this event";
  return (
    <Button
      size="sm"
      variant="outline"
      disabled={!eventId || setRelease.isPending}
      onClick={() => {
        const question = published
          ? `Withdraw ${label}'s weights? Teams lose access to the report until you publish again.`
          : `Publish ${label}'s weights to teams? They'll see the fleet report from now on.`;
        if (!window.confirm(question)) return;
        setRelease.mutateAsync(!published).then(
          () => toast.success(published ? "Weights withdrawn" : "Weights published to teams"),
          (err: Error) => toast.error(err.message),
        );
      }}
    >
      {published ? (
        <>
          <IconEyeOff className="h-4 w-4" /> Withdraw
        </>
      ) : (
        <>
          <IconEye className="h-4 w-4" /> Publish to teams
        </>
      )}
    </Button>
  );
}

export function FleetReportPage() {
  const { data } = useFleetReport();
  const { can } = useAuth();
  const canPublish = can("manage-weighings");

  if (!data) {
    return <div className="py-16 text-center text-sm text-muted-foreground">Loading fleet report…</div>;
  }

  const weightBand = data.summary.weight_tolerance_kg?.toFixed(0) ?? "—";
  // Ops always gets the numbers; a viewer with an empty board is being held
  // back deliberately, so say so rather than implying the fleet has no data.
  const withheld = !canPublish && data.weights_released_at == null;

  if (withheld) {
    return (
      <div className="space-y-4">
        <PageHeader title="Fleet Weight Report" />
        <div className="bg-card border border-border rounded-lg py-16 text-center">
          <p className="text-sm font-medium">
            {data.event_name
              ? `${data.event_name} weights haven't been published yet.`
              : "Weights haven't been published yet."}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            The report appears here once the tech team publishes it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Fleet Weight Report"
        subtitle={`Latest signed-off weighings · correctors as installed · updated ${formatDate(data.generated_at)}`}
        action={
          canPublish ? (
            <PublishButton
              eventId={data.event_id ?? null}
              eventName={data.event_name ?? null}
              releasedAt={data.weights_released_at ?? null}
            />
          ) : undefined
        }
      />

      {canPublish && data.weights_released_at == null && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-medium text-amber-700 dark:text-amber-400">
          Not published — teams can't see this report until you publish it.
        </div>
      )}

      <SummaryCards summary={data.summary} />

      <div className="hidden sm:block">
        <ReportTable entries={data.boats} summary={data.summary} />
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground mt-2">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-emerald-100 border border-emerald-300 dark:bg-emerald-950 dark:border-emerald-800" />
            final weight within ±{weightBand} kg of fleet mean
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-amber-100 border border-amber-300 dark:bg-amber-950 dark:border-amber-800" />
            beyond ±{weightBand} kg · TCG beyond ±{TCG_CUE_BAND_M} m
          </span>
          <span>REF — reference boat (heaviest bare)</span>
          {data.boats.some((b) => b.platform_gate_ok === false) && (
            <span>GATE — platform weighed outside the repeatability gate (signed off with override)</span>
          )}
        </div>
      </div>

      <div className="sm:hidden">
        <MobileList entries={data.boats} summary={data.summary} />
      </div>
    </div>
  );
}
